import { AppstoreAddOutlined, BarcodeOutlined } from "@ant-design/icons";
import { Button, Modal, message, Tooltip } from "antd";
import moment from "moment";
import { API, COMMONS } from "@/utils";
import "moment/locale/ja";
import { useBetterFeatureClient } from "@di-devs/utils-fe";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import MemberCameraScanModalComponent from "@/components/admin/Member/CameraScanModal";
import MemberDeviceScanModalComponent from "@/components/admin/Member/DeviceScanModal";
import MemberDeviceSelectModalComponent from "@/components/admin/Member/DeviceSelectModal";
import MemberPointUpdateModal from "@/components/admin/Member/MemberPointUpdateModal";
import TapAnimation from "@/components/common/TapAnimation";
import type { FeatureClient } from "@/config/client";
import { useSSEEvent } from "@/context/SSEContext";

dayjs.extend(relativeTime);
moment.locale("ja");

const MemberVisit = (props: Props) => {
	const { publicSettings } = props;

	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const reactFeatureClient = useBetterFeatureClient<FeatureClient>();

	const memberId = searchParams.get("memberId");

	const isMountedRef = COMMONS.USE_IS_MOUNTED_REF();
	const membersRef = useRef<any>();

	const [currentMember, setCurrentMember] = useState<any>({});
	const [confirmMember, setConfirmMember] = useState<any>({});
	const [renewPickerValue, setRenewPickerValue] = useState(dayjs().toDate());
	const [renewConfirmPickerValue, setRenewConfirmPickerValue] = useState(
		dayjs().toDate(),
	);

	const [isMemberVisit, setIsMemberVisit] = useState(false);

	const [isMemberDetailModalVisible, setIsMemberDetailModalVisible] =
		useState(false);
	const [isMemberRenewSelectModalVisible, setIsMemberRenewSelectModalVisible] =
		useState(false);
	const [isMemberRenewDeviceModalVisible, setIsMemberRenewDeviceModalVisible] =
		useState<any>(false);
	const [isMemberRenewCameraModalVisible, setIsMemberRenewCameraModalVisible] =
		useState(false);
	const [
		isMemberRenewConfirmModalVisible,
		setIsMemberRenewConfirmModalVisible,
	] = useState(false);
	const [isMemberEditModalVisible, setIsMemberEditModalVisible] =
		useState(false);

	const [modal, contextHolder] = Modal.useModal();

	const memberGetMutation = useMutation({
		mutationFn: reactFeatureClient.getMember,
		onSuccess: (response: any) => {
			setCurrentMember(response?.data || {});
		},
	});
	const memberEditMutation = useMutation({
		mutationFn: (data: any) =>
			reactFeatureClient.updateMember(data?.["memberId"], data),
		onSuccess: () => {
			message.success(COMMONS.SUCCESS_UPDATE_MSG);
			hideMemberEditModal();
			hideMemberRenewConfirmModal();
			queryClient.invalidateQueries({ queryKey: [API.QUERY_KEY_ADMIN_MEMBERS] });
		},
		onError: (error: FetchError) => {
			if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
				navigate(COMMONS.PERMISSION_ERROR_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
				message.warning(COMMONS.ERROR_SESSION_MSG);
				navigate(COMMONS.ADMIN_LOGIN_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
				message.error(COMMONS.ERROR_SYSTEM_MSG);
			} else {
				message.error(COMMONS.ERROR_SYSTEM_MSG);
			}
		},
	});

	const memberHandler = useCallback((response: { memberId?: number | string | null; data?: any }) => {
		if (response?.memberId && response?.data) {
			// Optimistic update for member detail
			queryClient.setQueryData(
				[API.QUERY_KEY_ADMIN_MEMBER_DETAIL, String(response.memberId)],
				(oldData: any) => {
					if (!oldData) return oldData;
					return {
						...oldData,
						data: { ...oldData.data, ...response.data },
					};
				}
			);
			
			// Optimistic update for member list (if present in list)
			queryClient.setQueriesData(
				{ queryKey: [API.QUERY_KEY_ADMIN_MEMBERS] },
				(oldData: any) => {
					if (!oldData || !oldData.data || !oldData.data.rows) return oldData;
					const newRows = oldData.data.rows.map((member: any) => {
						if (String(member.memberId) === String(response.memberId)) {
							return { ...member, ...response.data };
						}
						return member;
					});
					return {
						...oldData,
						data: { ...oldData.data, rows: newRows },
					};
				}
			);
		} else {
			// Fallback to invalidation
			queryClient.invalidateQueries({
				queryKey: [API.QUERY_KEY_ADMIN_MEMBERS],
			});
			if (response?.memberId) {
				queryClient.invalidateQueries({ queryKey: [API.QUERY_KEY_ADMIN_MEMBER_DETAIL, response.memberId] });
			}
		}
	}, [queryClient]);

	const chatHandler = useCallback((response: FetchResponse) => {
		if (
			membersRef.current?.find(
				(m: { memberId: number }) => m?.memberId === response?.memberId,
			)
		) {
			// Update member list for both new messages and read events
			queryClient.invalidateQueries({
				queryKey: [API.QUERY_KEY_ADMIN_MEMBERS],
			});

			if (response?.type === "read") {
				console.log(
					`Member ${response.memberId} messages marked as read - updating member visit list`,
				);
			}
		}
	}, [queryClient]);

	useSSEEvent(API.SSE_CHAT, chatHandler);
	useSSEEvent(API.SSE_MEMBER, memberHandler);

	const handleMemberDetail = (memberId: number) => {
		const paramData: any = {
			memberId: memberId,
		};

		memberGetMutation.mutate(paramData);
	};

	const showMemberDetailModal = (memberId: number) => {
		handleMemberDetail(memberId);
		setIsMemberDetailModalVisible(true);
	};

	const hideMemberDetailModal = () => {
		setCurrentMember({});
		setIsMemberDetailModalVisible(false);
	};

	const showMemberRenewSelectModal = (isVisit = false) => {
		setIsMemberVisit(isVisit);
		setIsMemberRenewSelectModalVisible(true);
	};
	const hideMemberRenewSelectModal = () => {
		setIsMemberRenewSelectModalVisible(false);
	};
	const showMemberRenewDeviceModal = () => {
		hideMemberRenewSelectModal();
		setIsMemberRenewDeviceModalVisible(true);
	};
	const hideMemberRenewDeviceModal = () => {
		setIsMemberRenewDeviceModalVisible(false);
	};

	const showMemberEditModal = (memberId: any) => {
		handleMemberDetail(memberId);
		setIsMemberEditModalVisible(true);
	};
	const hideMemberEditModal = () => {
		setCurrentMember({});
		setIsMemberEditModalVisible(false);
	};

	const showMemberRenewCameraModal = () => {
		hideMemberRenewSelectModal();
		setIsMemberRenewCameraModalVisible(true);
	};

	const hideMemberRenewCameraModal = () => {
		setIsMemberRenewCameraModalVisible(false);
	};

	const showMemberRenewConfirmModal = (member: any = {}) => {
		setConfirmMember(member);
		hideMemberRenewDeviceModal();
		hideMemberRenewCameraModal();

		if (!isMemberVisit) {
			setRenewConfirmPickerValue(
				member?.activeUntil
					? dayjs(member?.activeUntil)
							.add(1, "year")
							.subtract(1, "day")
							.toDate()
					: dayjs(member?.memberSince)
							.add(1, "year")
							.subtract(1, "day")
							.toDate(),
			);
		}
		if (!isMemberVisit) {
			setRenewPickerValue(
				member?.activeUntil
					? dayjs(member?.activeUntil)
							.add(1, "year")
							.subtract(1, "day")
							.toDate()
					: dayjs(member?.memberSince)
							.add(1, "year")
							.subtract(1, "day")
							.toDate(),
			);
		}

		setIsMemberRenewConfirmModalVisible(true);
	};

	const hideMemberRenewConfirmModal = () => {
		setConfirmMember({});
		setIsMemberVisit(false);
		setIsMemberRenewConfirmModalVisible(false);
	};

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			// Alt + C to open Visit Record
			if (event.altKey && (event.key === "c" || event.key === "C")) {
				event.preventDefault();
				showMemberRenewSelectModal(true);
			}
		};

		window.addEventListener("keydown", handleKeyDown);

		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, []);

	return (
		<>
			<div className="inline-flex">
				<TapAnimation>
					<Tooltip title="Alt + C">
						<Button
							type="primary"
							size="large"
							icon={<BarcodeOutlined rev={undefined} />}
							onClick={() => {
								showMemberRenewSelectModal(true);
							}}
							className="w-32 mx-4"
						>
							来店記録
						</Button>
					</Tooltip>
				</TapAnimation>

				<TapAnimation>
					<Button
						type="primary"
						size="large"
						icon={<AppstoreAddOutlined rev={undefined} />}
						onClick={() => {
							showMemberRenewSelectModal();
						}}
						className="w-32"
					>
						会員更新
					</Button>
				</TapAnimation>
			</div>

			<MemberDeviceSelectModalComponent
				{...props}
				isMemberVisit={isMemberVisit}
				showMemberRenewDeviceModal={showMemberRenewDeviceModal}
				isMemberRenewSelectModalVisible={isMemberRenewSelectModalVisible}
				hideMemberRenewSelectModal={hideMemberRenewSelectModal}
				showMemberRenewCameraModal={showMemberRenewCameraModal}
			/>
			<MemberDeviceScanModalComponent
				{...props}
				isMemberVisit={isMemberVisit}
				isMemberRenewDeviceModalVisible={isMemberRenewDeviceModalVisible}
				hideMemberRenewDeviceModal={hideMemberRenewDeviceModal}
				showMemberRenewConfirmModal={showMemberRenewConfirmModal}
			/>
			<MemberCameraScanModalComponent
				{...props}
				isMemberRenewCameraModalVisible={isMemberRenewCameraModalVisible}
				hideMemberRenewCameraModal={hideMemberRenewCameraModal}
				showMemberRenewConfirmModal={showMemberRenewConfirmModal}
				isMemberVisit={isMemberVisit}
			/>
			<MemberPointUpdateModal
				{...props}
				confirmMember={confirmMember}
				isMemberVisit={isMemberVisit}
				hideMemberRenewConfirmModal={hideMemberRenewConfirmModal}
				memberEditMutation={memberEditMutation}
				renewConfirmPickerValue={renewConfirmPickerValue}
				setRenewConfirmPickerValue={setRenewConfirmPickerValue}
				isMemberRenewConfirmModalVisible={isMemberRenewConfirmModalVisible}
			/>
			{contextHolder}
		</>
	);
};

export default MemberVisit;
